import * as React from 'react';
import FirstUpper from '../FirstUpper';
import { useAppContext } from '../../Services/AppContext';
import { useEffectAsync } from '../../helpers';
import apiClient from '../../Services/ApiClient';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CASE_DETAIL_QUERY, CASE_LIST_ENTRIES } from './Case.query';
import Loading from '../Loading';
import CaseChatEntry from './CaseChatEntry';
import ScrollBar from 'react-perfect-scrollbar';
import CaseChatCreateMessage from './CaseChatCreateMessage';
import OverlayLoading from '../OverlayLoading';
import { Case, useLazyCaseChatEntriesQuery, User } from '../../graphql/graphql';
import CaseTakeOverModal from '../../Modules/Case/Components/CaseTakeOver/CaseTakeOverModal';
import { Theme } from '../../Enums/Theme.enum';
// @ts-ignore
import styles from './CaseChat.module.scss';
import Button from '../Button/Button';
import Text from '../Base/Text/Text';
import CaseChatPatientIsTyping from '../../Modules/CaseChat/Components/CaseChatPatientIsTyping';

interface CaseChatProps {
	userId: string;
	caseId: string;
	isPreview?: boolean;
}

export default function CaseChat(props: CaseChatProps) {
	const { translation, socket } = useAppContext();
	const { userId, caseId, isPreview } = props;
	const [caseDetail, setCaseDetail] = useState<Case | undefined>(undefined);
	const [entries, setEntries] = useState<object[] | null>(null);
	const [processing, setProcessing] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const scrollParentRef = useRef<HTMLDivElement>(null);
	const [getCaseEntries] = useLazyCaseChatEntriesQuery();
	const [reload, setReload] = useState<number>(null);
	const [isMyCase, setIsMyCase] = useState(false);
	const [isCaseTakeOverOpen, setIsCaseTakeOverOpen] = useState(false);
	const [patient, setPatient] = useState<User | undefined>(undefined);

	const scrollChat = (soft: boolean) => {
		const parent = scrollParentRef.current;

		if (parent && scrollRef.current) {
			const scrollTop = parent.scrollTop;
			const clientHeight = parent.clientHeight;
			const scrollHeight = parent.scrollHeight;

			const distanceToBottom = scrollHeight - scrollTop - clientHeight;

			if (!soft || distanceToBottom < 60) {
				scrollRef.current.scrollIntoView({ behavior: 'smooth' });
			}
		}
	};

	// @ts-ignore
	useEffectAsync(async () => {
		const result = await apiClient.graphql({
			query: CASE_DETAIL_QUERY,
			variables: { id: caseId },
			fetchPolicy: 'network-only',
		});
		setIsMyCase(result?.data?.case.isMyCase);
		setCaseDetail(result?.data?.case);
		setPatient(result?.data?.case?.patient);
	});

	// @ts-ignore
	const loadEntries = async () => {
		const result = await getCaseEntries({
			id: caseId,
		}).unwrap();

		setEntries(result?.caseEntries?.data || []);
	};

	const beforeProcess = () => {
		setProcessing(true);
	};

	// @ts-ignore
	const afterProcess = async () => {
		await loadEntries();
		setProcessing(false);
	};

	// @ts-ignore
	useEffectAsync(async () => {
		await loadEntries();
	});

	useLayoutEffect(() => {
		scrollChat(false);
	});

	useEffectAsync(async () => {
		if (!reload) {
			return;
		}

		await loadEntries();
	}, [reload]);

	useEffect(() => {
		function updateFile(fileId: string, newData: any) {
			const tempData = entries ? [...entries] : undefined;
			if (tempData) {
				let updated = false;
				for (const tempItemKey in tempData) {
					const tempItem = tempData[tempItemKey];
					if (tempItem.fileId === fileId) {
						tempData[tempItemKey] = { ...tempItem, ...newData };
						updated = true;
					}
				}
				setEntries(tempData);
			}
		}

		function handleMessageEvent(data: {
			newMessageInCase?: string;
			generatedNewThumb?: string;
			avResult?: string;
			updatedData?: any;
		}) {
			if (
				typeof data.newMessageInCase !== 'undefined' &&
				typeof data.newMessageInCase !== caseId
			) {
				setTimeout(() => {
					setReload(+new Date());
				}, 500);
			} else if (typeof data.generatedNewThumb !== 'undefined') {
				entries?.map(caseEntry => {
					if (caseEntry.fileId === data.generatedNewThumb) {
						updateFile(caseEntry.fileId, data.updatedData);
					}
				});
			} else if (typeof data.avResult !== 'undefined') {
				entries?.map(caseEntry => {
					if (caseEntry.fileId === data.avResult) {
						updateFile(caseEntry.fileId, data.updatedData);
					}
				});
			}
		}

		function markEntryAsSeen(entryIds: [string], userId: string) {
			const tempData = entries ? [...entries] : undefined;
			if (tempData) {
				let updated = false;
				for (const tempItemKey in tempData) {
					const tempItem = tempData[tempItemKey];
					if (entryIds.indexOf(tempItem.id.toString()) !== -1) {
						const updateCaseEntryMembers = [];
						for (const entryMember of tempItem.caseEntryMembers) {
							const updatedEntryMember = { ...entryMember };
							if (updatedEntryMember.user.id === userId) {
								updatedEntryMember.seen = true;
							}
							updateCaseEntryMembers.push(updatedEntryMember);
						}
						tempData[tempItemKey] = {
							...tempItem,
							caseEntryMembers: updateCaseEntryMembers,
						};
						updated = true;
					}
				}
				setEntries(tempData);
			}
		}

		const handleSeenEvent = (data: {
			caseEntryIds: [string];
			caseId: string;
			userId: string;
		}) => {
			if (caseId !== data.caseId) {
				return;
			}

			markEntryAsSeen(data.caseEntryIds, data.userId);
		};

		socket.on('message', handleMessageEvent);
		socket.on('caseEntrySeen', handleSeenEvent);
		return () => {
			socket.off('message', handleMessageEvent);
			socket.off('caseEntrySeen', handleSeenEvent);
		};
	}, [entries]);

	if (!caseDetail) {
		return (
			<div className={'py-5'}>
				<Loading />
			</div>
		);
	}

	const isSolved = caseDetail.solved;
	return (
		<div
			className='case-detail-chat-window chat-window d-flex flex-grow-1 flex-column'
			id='callWindowChanger'
			style={{ position: 'relative' }}>
			{!isPreview && isMyCase && (
				<div className='treatment-page-layout__chat__controls'>
					{!isSolved && (
						<button className='btn btn-green-global remastered startCall'>
							<FirstUpper>{translation['base.shared.wannaCall']}</FirstUpper>
						</button>
					)}
					{!isSolved && (
						<button className='btn btn-green-global remastered startVideoCall'>
							<FirstUpper>
								{translation['base.shared.wannaVideoCall']}
							</FirstUpper>
						</button>
					)}
				</div>
			)}
			<div
				className='d-flex flex-column rounded-messages flex-grow-1'
				ref={scrollParentRef}>
				<div className='position-relative'>
					{(entries?.length || 0) > 0 ? (
						<ScrollBar style={{ width: '100%' }}>
							<div className='messages-block chat-messages px-3 pt-3 px-lg-5 pt-lg-5'>
								{entries?.map(entry => (
									<CaseChatEntry
										key={entry.id}
										entry={entry}
										myUserId={userId}
										beforeChange={beforeProcess}
										afterChange={afterProcess}
									/>
								))}
								{patient && caseDetail.patientId && (
									<CaseChatPatientIsTyping
										patientId={caseDetail.patientId}
										caseId={caseId}
										profileImageUrl={patient.profileImageUrl ?? undefined}
										scrollChat={() => scrollChat(true)}
									/>
								)}
								<div ref={scrollRef} />
							</div>
						</ScrollBar>
					) : (
						<Text
							className={styles.noAccessMessage}
							tx='component.caseChat.noAccessToHistory'
						/>
					)}
				</div>
			</div>
			{isMyCase && !isPreview && (
				<CaseChatCreateMessage
					userId={userId}
					caseId={caseId}
					beforeMessage={beforeProcess}
					onMessage={afterProcess}
					patient={patient}
					patientId={caseDetail.patientId}
				/>
			)}
			{!isMyCase && !isPreview && (
				<>
					<div className={styles.caseTakeOverWrapper}>
						<Text
							className={styles.text}
							tx='component.caseChat.youNeedToTakeOverOfCaseFirst'
						/>
						<Button
							title='base.takeOverTheCase'
							theme={Theme.GREEN}
							onClick={() => setIsCaseTakeOverOpen(true)}
						/>
					</div>
					{isCaseTakeOverOpen && (
						<CaseTakeOverModal
							isOpen
							caseId={caseId}
							onClose={() => setIsCaseTakeOverOpen(false)}
						/>
					)}
				</>
			)}
			{processing && <OverlayLoading />}
		</div>
	);
}
